import { Link } from 'react-router-dom';
import LegalDocumentLayout from '@/app/Legal/LegalDocumentLayout';

const DataDeletionPage = () => (
  <LegalDocumentLayout
    title="Data Deletion"
    description="Instructions for requesting deletion of your DwellBot Games and PlayClaw.Live account data, including Facebook Login data."
  >
    <p>
      <strong>Company:</strong> Dwell Bot Inc. (DBA DwellBot Games)
      <br />
      <strong>Services:</strong> DwellBot Games and PlayClaw.Live
      <br />
      <strong>Contact:</strong> <a href="mailto:privacy@dwell.bot">privacy@dwell.bot</a>
    </p>

    <p>
      This page explains how to request deletion of your account and personal data associated with DwellBot Games and
      PlayClaw.Live, including data linked to Facebook Login.
    </p>

    <hr />

    <h2>Option 1: Delete your account in the app (if available)</h2>
    <p>If the app provides an in-product deletion option:</p>
    <ol>
      <li>Sign in to your account.</li>
      <li>
        Go to <strong>Settings -&gt; Account -&gt; Delete Account</strong>.
      </li>
      <li>Follow the prompts to confirm deletion.</li>
    </ol>
    <p>If you do not see this option, use Option 2 below.</p>

    <hr />

    <h2>Option 2: Request deletion by email</h2>
    <p>
      Email <a href="mailto:privacy@dwell.bot">privacy@dwell.bot</a> from the email address associated with your
      account.
    </p>
    <p>
      <strong>Subject:</strong> Data Deletion Request
    </p>
    <p>In your message, include:</p>
    <ul>
      <li>The email address used to sign in to DwellBot Games / PlayClaw.Live</li>
      <li>A clear request (example: &quot;Please delete my account and associated personal data.&quot;)</li>
    </ul>
    <p>We may ask for additional information to verify you are the account owner.</p>

    <hr />

    <h2>Option 3: If you used Facebook Login</h2>
    <p>
      To request deletion of data associated with Facebook Login, email{' '}
      <a href="mailto:privacy@dwell.bot">privacy@dwell.bot</a>.
    </p>
    <p>
      <strong>Subject:</strong> Facebook Login Data Deletion Request
    </p>
    <p>Include:</p>
    <ul>
      <li>The email address used in our Services (if known)</li>
      <li>Your Facebook name and/or Facebook user ID (if available)</li>
      <li>Any other details that help us locate your account (e.g., username/handle)</li>
    </ul>

    <p>You can also remove the app&apos;s access from Facebook settings:</p>
    <ul>
      <li>Facebook Settings &amp; Privacy -&gt; Settings -&gt; Apps and Websites -&gt; Logged in with Facebook</li>
      <li>Select DwellBot Games / PlayClaw.Live and remove access</li>
    </ul>

    <blockquote>
      <p>
        Removing the app from Facebook stops further sharing from Facebook to our app, but it may not delete data
        already stored in your DwellBot account. For full deletion, please email us as described above.
      </p>
    </blockquote>

    <hr />

    <h2>What we delete (and what we may retain)</h2>
    <p>When we delete your account, we delete or anonymize personal data such as:</p>
    <ul>
      <li>Account profile details (e.g., email, name, profile image)</li>
      <li>Authentication identifiers (including social login identifiers)</li>
      <li>Service-related user content you provided (where applicable)</li>
    </ul>

    <p>We may retain limited information where required for:</p>
    <ul>
      <li>Legal compliance</li>
      <li>Security and fraud prevention</li>
      <li>Resolving disputes</li>
      <li>Backups (which are overwritten on a rolling basis)</li>
    </ul>

    <hr />

    <h2>Questions</h2>
    <p>
      If you have any questions about deletion, contact <a href="mailto:privacy@dwell.bot">privacy@dwell.bot</a>.
    </p>

    <hr />

    <p>
      <Link to="/privacy-policy">View Privacy Policy</Link>
      <br />
      <Link to="/terms-of-service">View Terms of Service</Link>
    </p>
  </LegalDocumentLayout>
);

export default DataDeletionPage;
